import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import DesktopLayout from "@/components/Layout/DesktopLayout";
import { api, formatApiError } from "@/api/client";
import { toast } from "sonner";
import { Loader2, Plus, Save, Trash2, Settings as SettingsIcon, Layers, Building2, Calculator, Upload, Image as ImageIcon, Cloud, CheckCircle2, XCircle, ExternalLink, Search, X, Check } from "lucide-react";

const FIELD = "w-full bg-white border border-slate-300 rounded-sm p-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none";
const LABEL = "hk-tiny-label block mb-1";

const TABS = [
  { key: "materials",   label: "Materials",   icon: Layers },
  { key: "door_types",  label: "Door Types",  icon: Layers },
  { key: "handles",     label: "Handles",     icon: Layers },
  { key: "accessories", label: "Accessories", icon: Layers },
  { key: "modules",     label: "Modules",     icon: SettingsIcon },
  { key: "panels",      label: "Panel Types", icon: SettingsIcon },
  { key: "constants",   label: "Constants",   icon: Calculator },
  { key: "company",     label: "Company & T&C", icon: Building2 },
  { key: "drive",       label: "Google Drive", icon: Cloud },
];

/** Inner panel without the page chrome — embedded in the Quote tab of /settings. */
export function QuoteSettingsPanel() {
  const [tab, setTab] = useState("materials");
  const [catalog, setCatalog] = useState(null);
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/quote/catalog");
      setCatalog(data);
    } catch (e) { setErr(formatApiError(e)); }
  };

  useEffect(() => { load(); }, []);

  if (err) return <div className="text-red-600">{err}</div>;
  if (!catalog) return <div className="text-slate-500">Loading catalog…</div>;

  return (
    <div data-testid="quote-settings-panel">
      <div className="flex gap-1 flex-wrap border-b border-slate-200 mb-6 overflow-x-auto">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`qs-tab-${t.key}`}
            className={`inline-flex items-center gap-2 px-3 py-2 text-xs font-bold border-b-2 -mb-px transition-colors whitespace-nowrap ${
              tab === t.key ? "border-orange-500 text-orange-600" : "border-transparent text-slate-500 hover:text-slate-800"
            }`}
          >
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "materials"   && <SimpleListEditor coll="materials"   rateField="rate_per_kg"   rateLabel="₹/kg"    items={catalog.materials}   onReload={load} />}
      {tab === "door_types"  && <SimpleListEditor coll="door-types"  rateField="rate_per_sqft" rateLabel="₹/sqft"  items={catalog.door_types}  onReload={load} />}
      {tab === "handles"     && <SimpleListEditor coll="handles"     rateField="rate_per_unit" rateLabel="₹/unit"  items={catalog.handles}     onReload={load} />}
      {tab === "accessories" && <AccessoriesEditor items={catalog.accessories} modules={catalog.modules} onReload={load} />}
      {tab === "modules"     && <ModulesEditor modules={catalog.modules} panelTypes={catalog.panel_types} onReload={load} />}
      {tab === "panels"      && <PanelTypesEditor items={catalog.panel_types} onReload={load} />}
      {tab === "constants"   && <ConstantsEditor values={catalog.constants} onReload={load} />}
      {tab === "company"     && <CompanyEditor info={catalog.company_info} onReload={load} />}
      {tab === "drive"       && <DriveStatus />}
    </div>
  );
}

/** Legacy /quote-settings page — redirects to /settings?tab=quote. */
export default function QuoteSettings() {
  const navigate = useNavigate();
  useEffect(() => { navigate("/settings?tab=quote", { replace: true }); }, [navigate]);
  return <DesktopLayout><div className="text-slate-500">Redirecting to Settings…</div></DesktopLayout>;
}

// ---------- Simple list (materials / door types / handles) ----------
function SimpleListEditor({ coll, rateField, rateLabel, items, onReload }) {
  const [draft, setDraft] = useState({ name: "", rate: "" });
  const [busy, setBusy] = useState(false);

  const add = async () => {
    if (!draft.name) return toast.error("Name is required");
    setBusy(true);
    try {
      const payload = { name: draft.name, active: true, [rateField]: Number(draft.rate || 0) };
      await api.post(`/quote/${coll}`, payload);
      toast.success("Added");
      setDraft({ name: "", rate: "" });
      onReload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const update = async (id, name, rate) => {
    try {
      await api.patch(`/quote/${coll}/${id}`, { name, active: true, [rateField]: Number(rate || 0) });
      toast.success("Saved");
      onReload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      await api.delete(`/quote/${coll}/${id}`);
      toast.success("Deleted");
      onReload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid={`qs-${coll}`}>
      <div className="hk-card p-4 mb-4 flex gap-3 items-end flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <label className={LABEL}>Name</label>
          <input className={FIELD} value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} data-testid={`qs-${coll}-name`} />
        </div>
        <div className="w-40">
          <label className={LABEL}>Rate ({rateLabel})</label>
          <input type="number" className={FIELD} value={draft.rate} onChange={(e) => setDraft({ ...draft, rate: e.target.value })} data-testid={`qs-${coll}-rate`} />
        </div>
        <button onClick={add} disabled={busy} className="bg-orange-500 hover:bg-orange-600 text-white font-bold px-4 py-2 rounded-sm inline-flex items-center gap-2 disabled:opacity-50" data-testid={`qs-${coll}-add`}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add
        </button>
      </div>

      <ul className="space-y-2">
        {(items || []).map((it) => (
          <SimpleRow key={it.id} item={it} rateField={rateField} rateLabel={rateLabel} onSave={(n, r) => update(it.id, n, r)} onRemove={() => remove(it.id)} testidPrefix={coll} />
        ))}
        {(!items || items.length === 0) && <li className="text-slate-400 text-sm">Empty — add the first one above.</li>}
      </ul>
    </div>
  );
}

function SimpleRow({ item, rateField, rateLabel, onSave, onRemove, testidPrefix }) {
  const [name, setName] = useState(item.name || "");
  const [rate, setRate] = useState(item[rateField] || 0);
  const dirty = name !== item.name || Number(rate) !== Number(item[rateField] || 0);
  return (
    <li className="hk-card p-3 flex gap-3 items-center flex-wrap" data-testid={`qs-row-${testidPrefix}-${item.id}`}>
      <input className={`${FIELD} flex-1 min-w-[200px]`} value={name} onChange={(e) => setName(e.target.value)} data-testid={`qs-${testidPrefix}-name-${item.id}`} />
      <input type="number" className={`${FIELD} w-32`} value={rate} onChange={(e) => setRate(e.target.value)} title={rateLabel} data-testid={`qs-${testidPrefix}-rate-${item.id}`} />
      <button onClick={() => onSave(name, rate)} disabled={!dirty} className="bg-slate-800 hover:bg-slate-900 text-white font-bold px-3 py-1.5 rounded-sm text-sm disabled:opacity-30" data-testid={`qs-${testidPrefix}-save-${item.id}`}>
        <Save className="w-3.5 h-3.5 inline" /> Save
      </button>
      <button onClick={onRemove} className="text-red-600 hover:text-red-800" aria-label="Remove" data-testid={`qs-${testidPrefix}-delete-${item.id}`}>
        <Trash2 className="w-4 h-4" />
      </button>
    </li>
  );
}

// ---------- Accessories ----------
function AccessoriesEditor({ items, modules, onReload }) {
  const blank = { name: "", brand: "", model_number: "", rate: 0, allowed_module_ids: [], active: true };
  const [draft, setDraft] = useState(blank);
  const [busy, setBusy] = useState(false);

  const add = async () => {
    if (!draft.name) return toast.error("Name required");
    setBusy(true);
    try {
      await api.post("/quote/accessories", { ...draft, rate: Number(draft.rate || 0) });
      toast.success("Added");
      setDraft(blank);
      onReload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const update = async (id, payload) => {
    try {
      await api.patch(`/quote/accessories/${id}`, payload);
      toast.success("Saved");
      onReload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete accessory?")) return;
    await api.delete(`/quote/accessories/${id}`);
    toast.success("Deleted");
    onReload();
  };

  return (
    <div data-testid="qs-accessories">
      <div className="hk-card p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <Input label="Name" value={draft.name} onChange={(v) => setDraft({ ...draft, name: v })} testid="qs-acc-name" />
          <Input label="Brand" value={draft.brand} onChange={(v) => setDraft({ ...draft, brand: v })} />
          <Input label="Model #" value={draft.model_number} onChange={(v) => setDraft({ ...draft, model_number: v })} />
          <Input label="Rate (₹)" type="number" value={draft.rate} onChange={(v) => setDraft({ ...draft, rate: v })} testid="qs-acc-rate" />
          <div className="flex items-end">
            <button onClick={add} disabled={busy} className="bg-orange-500 hover:bg-orange-600 text-white font-bold px-4 py-2 rounded-sm inline-flex items-center gap-2 disabled:opacity-50 w-full justify-center" data-testid="qs-acc-add">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add
            </button>
          </div>
        </div>
        <div className="mt-3">
          <label className="hk-tiny-label block mb-1.5">Assign to modules</label>
          <ModuleMultiSelect
            modules={modules}
            selected={draft.allowed_module_ids}
            onChange={(ids) => setDraft({ ...draft, allowed_module_ids: ids })}
            testid="qs-acc-new-modules"
          />
        </div>
      </div>

      <ul className="space-y-2">
        {(items || []).map((it) => (
          <AccessoryRow key={it.id} item={it} modules={modules} onSave={(p) => update(it.id, p)} onRemove={() => remove(it.id)} />
        ))}
        {(!items || items.length === 0) && <li className="text-slate-400 text-sm">No accessories yet.</li>}
      </ul>
    </div>
  );
}

function AccessoryRow({ item, modules, onSave, onRemove }) {
  const [state, setState] = useState({
    name: item.name || "", brand: item.brand || "", model_number: item.model_number || "",
    rate: item.rate || 0, active: item.active !== false, allowed_module_ids: item.allowed_module_ids || [],
  });
  const dirty = JSON.stringify(state) !== JSON.stringify({
    name: item.name || "", brand: item.brand || "", model_number: item.model_number || "",
    rate: item.rate || 0, active: item.active !== false, allowed_module_ids: item.allowed_module_ids || [],
  });
  return (
    <li className="hk-card p-3" data-testid={`qs-row-acc-${item.id}`}>
      <div className="grid grid-cols-1 md:grid-cols-6 gap-2 items-end">
        <input className={FIELD} value={state.name} onChange={(e) => setState({ ...state, name: e.target.value })} placeholder="Name" />
        <input className={FIELD} value={state.brand} onChange={(e) => setState({ ...state, brand: e.target.value })} placeholder="Brand" />
        <input className={FIELD} value={state.model_number} onChange={(e) => setState({ ...state, model_number: e.target.value })} placeholder="Model" />
        <input className={FIELD} type="number" value={state.rate} onChange={(e) => setState({ ...state, rate: Number(e.target.value) })} placeholder="Rate" />
        <div className="text-xs text-slate-500 flex items-center">
          {state.allowed_module_ids.length === 0
            ? <span className="italic text-red-500">No modules — won&apos;t appear in quotes</span>
            : <span className="font-semibold text-slate-700">{state.allowed_module_ids.length} module(s) assigned</span>}
        </div>
        <div className="flex gap-1 justify-end">
          <button onClick={() => onSave(state)} disabled={!dirty} className="bg-slate-800 text-white font-bold px-3 py-1.5 rounded-sm text-sm disabled:opacity-30" data-testid={`qs-acc-save-${item.id}`}>
            <Save className="w-3.5 h-3.5 inline" /> Save
          </button>
          <button onClick={onRemove} className="text-red-600 hover:text-red-800" data-testid={`qs-acc-del-${item.id}`}><Trash2 className="w-4 h-4" /></button>
        </div>
      </div>
      <div className="mt-3 pt-3 border-t border-slate-100">
        <label className="hk-tiny-label block mb-1.5">Assign to modules</label>
        <ModuleMultiSelect
          modules={modules}
          selected={state.allowed_module_ids}
          onChange={(ids) => setState({ ...state, allowed_module_ids: ids })}
          testid={`qs-acc-modules-${item.id}`}
        />
      </div>
    </li>
  );
}

/**
 * Multi-select for modules, grouped by product → cabinet type.
 * When nothing is selected, the accessory is treated as available on every module.
 */
function ModuleMultiSelect({ modules, selected, onChange, testid }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const selectedSet = new Set(selected || []);
  const grouped = useMemo(() => {
    const t = {};
    for (const m of modules || []) {
      t[m.product] = t[m.product] || {};
      t[m.product][m.cabinet_type] = t[m.product][m.cabinet_type] || [];
      t[m.product][m.cabinet_type].push(m);
    }
    return t;
  }, [modules]);

  const toggle = (id) => {
    if (selectedSet.has(id)) onChange((selected || []).filter((x) => x !== id));
    else onChange([...(selected || []), id]);
  };
  const clearAll = () => onChange([]);
  const selectAllVisible = () => {
    const visible = (modules || [])
      .filter((m) => matches(m, q))
      .map((m) => m.id);
    onChange(Array.from(new Set([...(selected || []), ...visible])));
  };

  function matches(m, needle) {
    if (!needle) return true;
    const s = needle.toLowerCase();
    return (m.name || "").toLowerCase().includes(s)
      || (m.cabinet_type || "").toLowerCase().includes(s)
      || (m.product || "").toLowerCase().includes(s);
  }

  const selectedList = (modules || []).filter((m) => selectedSet.has(m.id));

  return (
    <div data-testid={testid}>
      {/* Chip list of currently-selected modules */}
      <div className="flex flex-wrap gap-1.5 mb-2 min-h-[28px]">
        {selectedList.length === 0 && (
          <span className="text-xs italic text-red-500">
            No modules assigned — this accessory will not appear in any quote until at least one module is picked.
          </span>
        )}
        {selectedList.map((m) => (
          <span
            key={m.id}
            className="inline-flex items-center gap-1 bg-orange-50 border border-orange-200 text-orange-800 text-xs font-semibold rounded-full pl-2.5 pr-1 py-0.5"
          >
            <span>{m.cabinet_type} · {m.name}</span>
            <button
              type="button"
              onClick={() => toggle(m.id)}
              className="p-0.5 rounded-full hover:bg-orange-100"
              aria-label={`Remove ${m.name}`}
              data-testid={`${testid}-chip-remove-${m.id}`}
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => setOpen((s) => !s)}
          className="text-xs font-bold px-3 py-1.5 rounded-sm border border-slate-300 hover:border-orange-500 hover:text-orange-600 inline-flex items-center gap-1"
          data-testid={`${testid}-toggle`}
        >
          <Plus className="w-3.5 h-3.5" /> {open ? "Close module picker" : "Pick modules"}
        </button>
        {selectedList.length > 0 && (
          <button
            type="button"
            onClick={clearAll}
            className="text-xs text-slate-500 hover:text-red-600 font-semibold"
            data-testid={`${testid}-clear`}
          >
            Clear all
          </button>
        )}
      </div>

      {open && (
        <div className="mt-2 border border-slate-200 rounded-sm bg-slate-50/50 p-2 max-h-72 overflow-y-auto">
          <div className="sticky top-0 bg-slate-50 pb-2 mb-2 border-b border-slate-200 flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search modules or cabinet type…"
                className="w-full pl-7 pr-2 py-1.5 text-xs border border-slate-300 rounded-sm bg-white focus:outline-none focus:border-orange-500"
                data-testid={`${testid}-search`}
              />
            </div>
            <button
              type="button"
              onClick={selectAllVisible}
              className="text-xs font-bold text-orange-600 hover:text-orange-700 whitespace-nowrap"
              data-testid={`${testid}-select-all`}
            >
              Select all shown
            </button>
          </div>
          {Object.keys(grouped).length === 0 && (
            <div className="text-xs text-slate-400 italic p-2">No modules in catalog yet.</div>
          )}
          {Object.keys(grouped).sort().map((product) => (
            <div key={product} className="mb-2 last:mb-0">
              <div className="hk-tiny-label px-1 mb-1">{product}</div>
              {Object.keys(grouped[product]).sort().map((cabinet) => {
                const list = grouped[product][cabinet].filter((m) => matches(m, q));
                if (list.length === 0) return null;
                return (
                  <div key={cabinet} className="mb-1.5 last:mb-0">
                    <div className="text-[11px] font-bold text-slate-500 px-1">{cabinet}</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4">
                      {list.map((m) => {
                        const on = selectedSet.has(m.id);
                        return (
                          <label
                            key={m.id}
                            className={`text-xs flex items-center gap-2 py-1 px-1.5 rounded-sm cursor-pointer hover:bg-white ${on ? "text-orange-700 font-semibold" : "text-slate-700"}`}
                            data-testid={`${testid}-opt-${m.id}`}
                          >
                            <input
                              type="checkbox"
                              checked={on}
                              onChange={() => toggle(m.id)}
                              className="accent-orange-500"
                            />
                            <span className="truncate">{m.name}</span>
                            {on && <Check className="w-3 h-3 shrink-0 text-orange-500" />}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- Modules ----------
function ModulesEditor({ modules, panelTypes, onReload }) {
  const tree = useMemo(() => {
    const t = {};
    for (const m of modules || []) {
      t[m.product] = t[m.product] || {};
      t[m.product][m.cabinet_type] = t[m.product][m.cabinet_type] || [];
      t[m.product][m.cabinet_type].push(m);
    }
    return t;
  }, [modules]);
  const [product, setProduct] = useState(Object.keys(tree)[0] || "");
  const [cabinet, setCabinet] = useState("");
  const [showAddCab, setShowAddCab] = useState(false);
  const [newProd, setNewProd] = useState("");
  const [newCab, setNewCab] = useState("");
  const [showAddMod, setShowAddMod] = useState(false);
  const [modDraft, setModDraft] = useState({ name: "", panels: [{ panel_key: (panelTypes[0] || {}).key || "side", quantity: 1, double_wall: false }] });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const cabs = Object.keys(tree[product] || {});
    if (cabs.length && !cabs.includes(cabinet)) setCabinet(cabs[0]);
  }, [product, tree, cabinet]);

  const list = (tree[product] || {})[cabinet] || [];

  const updateModule = async (id, payload) => {
    try {
      await api.patch(`/quote/modules/${id}`, payload);
      toast.success("Saved");
      onReload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteModule = async (id) => {
    if (!window.confirm("Permanently delete this module? Existing quotes that reference it will keep their snapshotted data.")) return;
    try {
      await api.delete(`/quote/modules/${id}`);
      toast.success("Deleted");
      onReload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const addCabinet = async () => {
    const p = (newProd || product).trim();
    const c = newCab.trim();
    if (!p || !c) return toast.error("Product and cabinet type are required");
    if ((tree[p] || {})[c]) return toast.error("This cabinet type already exists for this product");
    // Create a placeholder module under the new cabinet so it shows up everywhere
    setBusy(true);
    try {
      await api.post("/quote/modules", {
        name: `${c} placeholder (rename me)`,
        product: p,
        cabinet_type: c,
        panels: [{ panel_key: (panelTypes[0] || {}).key || "side", quantity: 1, double_wall: false }],
        active: true,
      });
      toast.success(`Cabinet type "${c}" added`);
      setShowAddCab(false);
      setNewProd("");
      setNewCab("");
      setProduct(p);
      setCabinet(c);
      onReload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const deleteCabinetType = async () => {
    if (!product || !cabinet) return toast.error("Pick a product and cabinet type first");
    const moduleCount = list.length;
    const msg = moduleCount > 0
      ? `Delete cabinet type "${cabinet}" under "${product}"?\n\nThis will permanently remove ${moduleCount} module(s) inside it. Existing quotes referencing these modules will keep their snapshotted data.`
      : `Delete empty cabinet type "${cabinet}" under "${product}"?`;
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      await api.delete("/quote/cabinet-types", {
        data: { product, cabinet_type: cabinet, cascade: moduleCount > 0 },
      });
      toast.success(`Cabinet type "${cabinet}" deleted`);
      setCabinet("");
      onReload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const addModule = async () => {
    if (!modDraft.name.trim()) return toast.error("Module name required");
    if (!product || !cabinet) return toast.error("Pick a product and cabinet type first");
    setBusy(true);
    try {
      await api.post("/quote/modules", {
        name: modDraft.name.trim(),
        product, cabinet_type: cabinet,
        panels: modDraft.panels.filter((p) => p.panel_key),
        active: true,
      });
      toast.success("Module added");
      setShowAddMod(false);
      setModDraft({ name: "", panels: [{ panel_key: (panelTypes[0] || {}).key || "side", quantity: 1, double_wall: false }] });
      onReload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const updateDraftPanel = (i, field, val) => {
    const next = [...modDraft.panels];
    next[i] = { ...next[i], [field]: field === "quantity" ? Number(val) : (field === "double_wall" ? !!val : val) };
    setModDraft({ ...modDraft, panels: next });
  };

  return (
    <div data-testid="qs-modules">
      <div className="flex gap-3 mb-4 flex-wrap items-end">
        <div>
          <label className={LABEL}>Product</label>
          <select className={FIELD} value={product} onChange={(e) => setProduct(e.target.value)} data-testid="qs-mod-product">
            {Object.keys(tree).map((p) => <option key={p}>{p}</option>)}
          </select>
        </div>
        <div>
          <label className={LABEL}>Cabinet Type</label>
          <select className={FIELD} value={cabinet} onChange={(e) => setCabinet(e.target.value)} data-testid="qs-mod-cabinet">
            {Object.keys(tree[product] || {}).map((c) => <option key={c}>{c}</option>)}
          </select>
        </div>
        <button onClick={() => setShowAddCab((s) => !s)} className="bg-white border border-slate-300 hover:bg-slate-50 text-slate-800 font-bold px-3 py-2 rounded-sm text-sm inline-flex items-center gap-1" data-testid="qs-mod-add-cabinet">
          <Plus className="w-4 h-4" /> Add cabinet type
        </button>
        <button
          onClick={deleteCabinetType}
          disabled={!product || !cabinet || busy}
          className="bg-white border border-slate-300 hover:border-red-500 hover:text-red-600 text-slate-700 font-bold px-3 py-2 rounded-sm text-sm inline-flex items-center gap-1 disabled:opacity-50"
          data-testid="qs-mod-delete-cabinet"
          title="Delete this cabinet type (and all its modules)"
        >
          <Trash2 className="w-4 h-4" /> Delete cabinet type
        </button>
        <button onClick={() => setShowAddMod((s) => !s)} className="bg-orange-500 hover:bg-orange-600 text-white font-bold px-3 py-2 rounded-sm text-sm inline-flex items-center gap-1" data-testid="qs-mod-add-module">
          <Plus className="w-4 h-4" /> Add module
        </button>
        <div className="text-sm text-slate-500 self-end ml-auto">{list.length} module(s)</div>
      </div>

      {showAddCab && (
        <div className="hk-card p-4 mb-4 grid grid-cols-1 md:grid-cols-3 gap-3 items-end" data-testid="qs-mod-new-cab-form">
          <div>
            <label className={LABEL}>Product</label>
            <input className={FIELD} placeholder={product || "e.g. Modular Kitchen"} value={newProd} onChange={(e) => setNewProd(e.target.value)} data-testid="qs-mod-new-cab-product" />
          </div>
          <div>
            <label className={LABEL}>New cabinet type</label>
            <input className={FIELD} placeholder="e.g. CORNER UNIT" value={newCab} onChange={(e) => setNewCab(e.target.value)} data-testid="qs-mod-new-cab-name" />
          </div>
          <button onClick={addCabinet} disabled={busy} className="bg-slate-800 hover:bg-slate-900 text-white font-bold px-4 py-2 rounded-sm text-sm inline-flex items-center gap-1 disabled:opacity-50" data-testid="qs-mod-new-cab-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create
          </button>
        </div>
      )}

      {showAddMod && (
        <div className="hk-card p-4 mb-4" data-testid="qs-mod-new-mod-form">
          <div className="text-xs hk-tiny-label mb-2">Add module under {product} → {cabinet}</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end mb-3">
            <div className="md:col-span-2">
              <label className={LABEL}>Module name</label>
              <input className={FIELD} value={modDraft.name} onChange={(e) => setModDraft({ ...modDraft, name: e.target.value })} placeholder="e.g. 24X24 BASE CABINET 1S 1D" data-testid="qs-mod-new-mod-name" />
            </div>
            <button onClick={addModule} disabled={busy} className="bg-orange-500 hover:bg-orange-600 text-white font-bold px-4 py-2 rounded-sm text-sm inline-flex items-center gap-2 disabled:opacity-50 md:justify-self-start" data-testid="qs-mod-new-mod-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add module
            </button>
          </div>

          <div className="text-xs hk-tiny-label mb-1">Panels</div>
          <div className="grid gap-2">
            {modDraft.panels.map((p, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center text-sm">
                <select className={`${FIELD} col-span-5`} value={p.panel_key} onChange={(e) => updateDraftPanel(i, "panel_key", e.target.value)}>
                  {panelTypes.map((pt) => <option key={pt.key} value={pt.key}>{pt.name}</option>)}
                </select>
                <input type="number" className={`${FIELD} col-span-2`} value={p.quantity} onChange={(e) => updateDraftPanel(i, "quantity", e.target.value)} placeholder="Qty" />
                <label className="col-span-3 flex items-center gap-2 text-xs">
                  <input type="checkbox" checked={!!p.double_wall} onChange={(e) => updateDraftPanel(i, "double_wall", e.target.checked)} />
                  Double-wall
                </label>
                <button onClick={() => setModDraft({ ...modDraft, panels: modDraft.panels.filter((_, idx) => idx !== i) })} className="col-span-2 text-red-600 hover:text-red-800 text-sm justify-self-end"><Trash2 className="w-4 h-4 inline" /></button>
              </div>
            ))}
            <button onClick={() => setModDraft({ ...modDraft, panels: [...modDraft.panels, { panel_key: (panelTypes[0] || {}).key || "side", quantity: 1, double_wall: false }] })} className="text-xs font-bold text-orange-600 hover:text-orange-700 self-start">+ Add panel</button>
          </div>
        </div>
      )}

      <ul className="space-y-2">
        {list.map((m) => (
          <ModuleRow key={m.id} module={m} panelTypes={panelTypes} onSave={(p) => updateModule(m.id, p)} onDisable={() => deleteModule(m.id)} />
        ))}
        {list.length === 0 && <li className="text-slate-400 text-sm">No modules in this cabinet type.</li>}
      </ul>
    </div>
  );
}

function ModuleRow({ module: m, panelTypes, onSave, onDisable }) {
  const [name, setName] = useState(m.name);
  const [panels, setPanels] = useState(m.panels || []);
  const dimsToString = (arr) => (Array.isArray(arr) ? arr.join(", ") : "");
  const [widthOptions, setWidthOptions] = useState(dimsToString(m.width_options));
  const [depthOptions, setDepthOptions] = useState(dimsToString(m.depth_options));
  const [heightOptions, setHeightOptions] = useState(dimsToString(m.height_options));

  const parseDims = (s) =>
    (s || "")
      .split(/[,\s]+/)
      .map((x) => x.trim())
      .filter(Boolean)
      .map((x) => Number(x))
      .filter((x) => !isNaN(x) && x > 0);

  const dirty =
    name !== m.name ||
    JSON.stringify(panels) !== JSON.stringify(m.panels || []) ||
    JSON.stringify(parseDims(widthOptions))  !== JSON.stringify(m.width_options  || []) ||
    JSON.stringify(parseDims(depthOptions))  !== JSON.stringify(m.depth_options  || []) ||
    JSON.stringify(parseDims(heightOptions)) !== JSON.stringify(m.height_options || []);

  const updatePanel = (i, field, val) => {
    const next = [...panels];
    next[i] = { ...next[i], [field]: field === "quantity" ? Number(val) : (field === "double_wall" ? !!val : val) };
    setPanels(next);
  };
  const addPanel = () => setPanels([...panels, { panel_key: (panelTypes[0] || {}).key || "side", quantity: 1, double_wall: false }]);
  const removePanel = (i) => setPanels(panels.filter((_, idx) => idx !== i));

  const save = () => onSave({
    name,
    panels,
    width_options:  parseDims(widthOptions),
    depth_options:  parseDims(depthOptions),
    height_options: parseDims(heightOptions),
  });

  return (
    <li className="hk-card p-4" data-testid={`qs-row-mod-${m.id}`}>
      <div className="flex gap-3 items-center mb-3 flex-wrap">
        <input className={`${FIELD} flex-1 min-w-[260px] font-bold`} value={name} onChange={(e) => setName(e.target.value)} />
        <button onClick={save} disabled={!dirty} className="bg-slate-800 text-white font-bold px-3 py-1.5 rounded-sm text-sm disabled:opacity-30" data-testid={`qs-mod-save-${m.id}`}>
          <Save className="w-3.5 h-3.5 inline" /> Save
        </button>
        <button onClick={onDisable} className="text-red-600 hover:text-red-800" title="Disable module"><Trash2 className="w-4 h-4" /></button>
      </div>

      {/* Per-module dimension option lists */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4 p-3 rounded-sm bg-slate-50 border border-slate-200">
        <div>
          <label className={LABEL}>Allowed widths (mm)</label>
          <input
            className={FIELD}
            value={widthOptions}
            onChange={(e) => setWidthOptions(e.target.value)}
            placeholder="e.g. 450, 600, 900"
            data-testid={`qs-mod-widths-${m.id}`}
          />
        </div>
        <div>
          <label className={LABEL}>Allowed depths (mm)</label>
          <input
            className={FIELD}
            value={depthOptions}
            onChange={(e) => setDepthOptions(e.target.value)}
            placeholder="e.g. 560, 580"
            data-testid={`qs-mod-depths-${m.id}`}
          />
        </div>
        <div>
          <label className={LABEL}>Allowed heights (mm)</label>
          <input
            className={FIELD}
            value={heightOptions}
            onChange={(e) => setHeightOptions(e.target.value)}
            placeholder="e.g. 720"
            data-testid={`qs-mod-heights-${m.id}`}
          />
        </div>
        <p className="text-[11px] text-slate-500 md:col-span-3 -mt-1">
          Comma-separated. When set, only these values will appear as dropdown choices in the Quote Builder for this module. Leave blank to allow any value.
        </p>
      </div>

      <div className="grid gap-2">
        {panels.map((p, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-center text-sm">
            <select className={`${FIELD} col-span-5`} value={p.panel_key} onChange={(e) => updatePanel(i, "panel_key", e.target.value)}>
              {panelTypes.map((pt) => <option key={pt.key} value={pt.key}>{pt.name}</option>)}
            </select>
            <input type="number" className={`${FIELD} col-span-2`} value={p.quantity} onChange={(e) => updatePanel(i, "quantity", e.target.value)} placeholder="Qty" />
            <label className="col-span-3 flex items-center gap-2 text-xs">
              <input type="checkbox" checked={!!p.double_wall} onChange={(e) => updatePanel(i, "double_wall", e.target.checked)} />
              Double-wall
            </label>
            <button onClick={() => removePanel(i)} className="col-span-2 text-red-600 hover:text-red-800 text-sm justify-self-end"><Trash2 className="w-4 h-4 inline" /></button>
          </div>
        ))}
        <button onClick={addPanel} className="text-xs font-bold text-orange-600 hover:text-orange-700 self-start">+ Add panel</button>
      </div>
    </li>
  );
}

// ---------- Panel types ----------
const DIM_CHOICES = [
  { value: "width",  label: "Width" },
  { value: "depth",  label: "Depth" },
  { value: "height", label: "Height" },
];

function PanelTypesEditor({ items, onReload }) {
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState({ key: "", name: "", dim1: "width", dim2: "height", buffer1: 0, buffer2: 0 });

  const create = async () => {
    if (!draft.key.trim() || !draft.name.trim()) return toast.error("Key and name are required");
    setBusy(true);
    try {
      await api.post("/quote/panel-types", draft);
      toast.success(`Panel type "${draft.name}" created`);
      setDraft({ key: "", name: "", dim1: "width", dim2: "height", buffer1: 0, buffer2: 0 });
      onReload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const update = async (key, payload) => {
    try {
      await api.patch(`/quote/panel-types/${key}`, payload);
      toast.success("Saved");
      onReload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete panel type "${item.name}"?\n\nThis cannot be undone. If any modules still reference this panel the delete will be blocked.`)) return;
    try {
      await api.delete(`/quote/panel-types/${item.key}`);
      toast.success(`"${item.name}" deleted`);
      onReload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="qs-panel-types">
      {/* Create form */}
      <div className="hk-card p-4 mb-5 border-l-4 border-l-orange-500" data-testid="qs-panel-new">
        <h4 className="font-bold text-sm mb-3" style={{ fontFamily: "Chivo" }}>Add a new panel type</h4>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
          <div className="md:col-span-2">
            <label className={LABEL}>Display name</label>
            <input
              className={FIELD}
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              placeholder="e.g. Wardrobe Backer"
              data-testid="qs-panel-new-name"
            />
          </div>
          <div>
            <label className={LABEL}>Key (slug)</label>
            <input
              className={FIELD + " font-mono"}
              value={draft.key}
              onChange={(e) => setDraft({ ...draft, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_") })}
              placeholder="wardrobe_backer"
              data-testid="qs-panel-new-key"
            />
          </div>
          <div>
            <label className={LABEL}>Dim 1</label>
            <select
              className={FIELD}
              value={draft.dim1}
              onChange={(e) => setDraft({ ...draft, dim1: e.target.value })}
              data-testid="qs-panel-new-dim1"
            >
              {DIM_CHOICES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <label className={LABEL}>Dim 2</label>
            <select
              className={FIELD}
              value={draft.dim2}
              onChange={(e) => setDraft({ ...draft, dim2: e.target.value })}
              data-testid="qs-panel-new-dim2"
            >
              {DIM_CHOICES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={create}
              disabled={busy}
              className="bg-orange-500 hover:bg-orange-600 text-white font-bold px-4 py-2 rounded-sm inline-flex items-center gap-2 disabled:opacity-50 w-full justify-center"
              data-testid="qs-panel-new-add"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add
            </button>
          </div>
          <div>
            <label className={LABEL}>Buffer 1 (mm)</label>
            <input
              type="number"
              className={FIELD}
              value={draft.buffer1}
              onChange={(e) => setDraft({ ...draft, buffer1: Number(e.target.value) })}
              data-testid="qs-panel-new-buf1"
            />
          </div>
          <div>
            <label className={LABEL}>Buffer 2 (mm)</label>
            <input
              type="number"
              className={FIELD}
              value={draft.buffer2}
              onChange={(e) => setDraft({ ...draft, buffer2: Number(e.target.value) })}
              data-testid="qs-panel-new-buf2"
            />
          </div>
        </div>
        <p className="text-[11px] text-slate-500 mt-2">
          Dim1 &amp; Dim2 pick which module measurement is used when computing sheet area for this panel.
          Buffers are added to each dimension before the area calc.
        </p>
      </div>

      <p className="text-xs text-slate-500 mb-3">Existing panel types — edit the name, dimensions, buffers or delete.</p>
      <ul className="space-y-2">
        {(items || []).map((p) => (
          <PanelRow key={p.key} item={p} onSave={(payload) => update(p.key, payload)} onDelete={() => remove(p)} />
        ))}
        {(items || []).length === 0 && <li className="text-slate-400 text-sm">No panel types yet.</li>}
      </ul>
    </div>
  );
}
function PanelRow({ item, onSave, onDelete }) {
  const [name, setName] = useState(item.name || "");
  const [dim1, setDim1] = useState(item.dim1 || "width");
  const [dim2, setDim2] = useState(item.dim2 || "height");
  const [b1, setB1] = useState(item.buffer1 ?? 0);
  const [b2, setB2] = useState(item.buffer2 ?? 0);
  const dirty =
    name !== (item.name || "") ||
    dim1 !== (item.dim1 || "width") ||
    dim2 !== (item.dim2 || "height") ||
    Number(b1) !== Number(item.buffer1 ?? 0) ||
    Number(b2) !== Number(item.buffer2 ?? 0);
  return (
    <li className="hk-card p-3" data-testid={`qs-row-panel-${item.key}`}>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end">
        <div className="md:col-span-3">
          <label className={LABEL}>Name</label>
          <input
            className={FIELD + " font-bold"}
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid={`qs-panel-name-${item.key}`}
          />
          <div className="text-[11px] text-slate-400 mt-1 font-mono">key: {item.key}</div>
        </div>
        <div className="md:col-span-2">
          <label className={LABEL}>Dim 1</label>
          <select
            className={FIELD}
            value={dim1}
            onChange={(e) => setDim1(e.target.value)}
            data-testid={`qs-panel-dim1-${item.key}`}
          >
            {DIM_CHOICES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>
        <div className="md:col-span-2">
          <label className={LABEL}>Dim 2</label>
          <select
            className={FIELD}
            value={dim2}
            onChange={(e) => setDim2(e.target.value)}
            data-testid={`qs-panel-dim2-${item.key}`}
          >
            {DIM_CHOICES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>
        <div className="md:col-span-2">
          <label className={LABEL}>Buffer 1 ({dim1})</label>
          <input type="number" className={FIELD} value={b1} onChange={(e) => setB1(e.target.value)} />
        </div>
        <div className="md:col-span-2">
          <label className={LABEL}>Buffer 2 ({dim2})</label>
          <input type="number" className={FIELD} value={b2} onChange={(e) => setB2(e.target.value)} />
        </div>
        <div className="flex gap-1 justify-end">
          <button
            onClick={() => onSave({ name, dim1, dim2, buffer1: Number(b1), buffer2: Number(b2) })}
            disabled={!dirty}
            className="bg-slate-800 text-white font-bold px-3 py-2 rounded-sm text-sm disabled:opacity-30"
            data-testid={`qs-panel-save-${item.key}`}
          >
            <Save className="w-3.5 h-3.5 inline" /> Save
          </button>
          <button
            onClick={onDelete}
            className="text-red-600 hover:text-red-800 p-2"
            title="Delete panel type"
            data-testid={`qs-panel-del-${item.key}`}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </li>
  );
}

// ---------- Constants ----------
function ConstantsEditor({ values, onReload }) {
  const [state, setState] = useState(values || {});
  useEffect(() => setState(values || {}), [values]);
  const fields = [
    { key: "specific_gravity",    label: "SS specific gravity (g/cm³)" },
    { key: "thickness_mm",        label: "Sheet thickness (mm)" },
    { key: "mm2_to_m2_factor",    label: "mm² → m² factor", step: "0.0000001" },
    { key: "mm2_to_sqft_factor",  label: "mm² → sqft factor", step: "0.0000001" },
  ];
  const save = async () => {
    try {
      await api.patch("/quote/constants", state);
      toast.success("Saved");
      onReload();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div data-testid="qs-constants" className="hk-card p-6 max-w-2xl">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {fields.map((f) => (
          <div key={f.key}>
            <label className={LABEL}>{f.label}</label>
            <input type="number" step={f.step || "any"} className={FIELD} value={state[f.key] ?? ""} onChange={(e) => setState({ ...state, [f.key]: Number(e.target.value) })} data-testid={`qs-const-${f.key}`} />
          </div>
        ))}
      </div>
      <button onClick={save} className="mt-4 bg-orange-500 hover:bg-orange-600 text-white font-bold px-5 py-2 rounded-sm inline-flex items-center gap-2" data-testid="qs-const-save">
        <Save className="w-4 h-4" /> Save constants
      </button>
    </div>
  );
}

// ---------- Company info & T&Cs ----------
function CompanyEditor({ info, onReload }) {
  const [state, setState] = useState(info || {});
  const [logoFile, setLogoFile] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => setState(info || {}), [info]);

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        company_name: state.company_name || "",
        address: state.address || "",
        phone: state.phone || "",
        email: state.email || "",
        gstin: state.gstin || "",
        terms_and_conditions: state.terms_and_conditions || "",
      };
      await api.patch("/quote/company-info", payload);
      if (logoFile) {
        const fd = new FormData();
        fd.append("file", logoFile);
        await api.post("/quote/company-info/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setLogoFile(null);
      }
      toast.success("Saved");
      onReload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="qs-company" className="hk-card p-6 max-w-3xl">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input label="Company name" value={state.company_name || ""} onChange={(v) => setState({ ...state, company_name: v })} testid="qs-co-name" />
        <Input label="GSTIN"        value={state.gstin || ""}        onChange={(v) => setState({ ...state, gstin: v })} testid="qs-co-gstin" />
        <Input label="Phone"        value={state.phone || ""}        onChange={(v) => setState({ ...state, phone: v })} testid="qs-co-phone" />
        <Input label="Email"        value={state.email || ""}        onChange={(v) => setState({ ...state, email: v })} testid="qs-co-email" />
        <div className="md:col-span-2">
          <label className={LABEL}>Address</label>
          <textarea className={FIELD} rows={2} value={state.address || ""} onChange={(e) => setState({ ...state, address: e.target.value })} data-testid="qs-co-address" />
        </div>
        <div className="md:col-span-2">
          <label className={LABEL}>Terms &amp; Conditions (rendered as plain text in the PDF)</label>
          <textarea className={FIELD} rows={6} value={state.terms_and_conditions || ""} onChange={(e) => setState({ ...state, terms_and_conditions: e.target.value })} data-testid="qs-co-tnc" />
        </div>
        <div className="md:col-span-2">
          <label className={LABEL}>Logo (PNG / JPG, max 5 MB)</label>
          <div className="flex items-center gap-3 flex-wrap">
            <label className="inline-flex items-center gap-2 bg-slate-800 hover:bg-slate-900 text-white font-bold px-4 py-2 rounded-sm text-sm cursor-pointer">
              <Upload className="w-4 h-4" />
              <span>{logoFile ? logoFile.name : "Choose file"}</span>
              <input type="file" accept="image/png,image/jpeg" className="hidden" onChange={(e) => setLogoFile(e.target.files?.[0] || null)} data-testid="qs-co-logo" />
            </label>
            {state.logo_path && <span className="text-xs text-slate-500 inline-flex items-center gap-1"><ImageIcon className="w-3 h-3" /> Current logo on file</span>}
          </div>
        </div>
      </div>

      <button onClick={save} disabled={busy} className="mt-5 bg-orange-500 hover:bg-orange-600 text-white font-bold px-5 py-2 rounded-sm inline-flex items-center gap-2 disabled:opacity-50" data-testid="qs-co-save">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save company info
      </button>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className={LABEL}>{label}</label>
      <input type={type} className={FIELD} value={value ?? ""} onChange={(e) => onChange(type === "number" ? e.target.value : e.target.value)} data-testid={testid} />
    </div>
  );
}

// ---------- Google Drive status ----------
function DriveStatus() {
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/integrations/gdrive/status");
        setInfo(data);
      } catch (e) { setErr(formatApiError(e)); }
    })();
  }, []);

  if (err) return <div className="text-red-600">{err}</div>;
  if (!info) return <div className="text-slate-500">Probing Drive…</div>;

  const ok = info.enabled && !info.error;

  return (
    <div className="hk-card p-6 max-w-2xl" data-testid="qs-drive">
      <div className="flex items-center gap-2 mb-4">
        <Cloud className="w-5 h-5 text-orange-500" />
        <h3 className="font-bold text-lg" style={{ fontFamily: "Chivo" }}>Google Drive backup</h3>
        {ok ? (
          <span className="hk-badge hk-badge-done inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Active</span>
        ) : (
          <span className="hk-badge hk-badge-overdue inline-flex items-center gap-1"><XCircle className="w-3 h-3" /> Not configured</span>
        )}
      </div>

      {ok ? (
        <>
          <p className="text-sm text-slate-600 mb-4">
            Every file uploaded via the project workflow is mirrored to your shared Drive in
            real time. Use this as an off-site backup &amp; an easy way to share assets with anyone outside the app.
          </p>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <Info label="Service account" value={info.service_account_email} mono />
            <Info label="Shared Drive" value={info.folder_name} />
            <Info label="Folder ID" value={info.folder_id} mono />
            <Info label="Type" value={info.is_shared_drive ? "Shared Drive (root)" : "Folder"} />
          </dl>
          <a
            href={`https://drive.google.com/drive/folders/${info.folder_id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-5 inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold px-4 py-2 rounded-sm text-sm"
            data-testid="qs-drive-open"
          >
            <ExternalLink className="w-4 h-4" /> Open Drive folder
          </a>
        </>
      ) : (
        <div className="text-sm text-slate-600 space-y-2">
          <p>Drive backup is disabled. Reason: <span className="font-mono text-red-600">{info.reason || info.error}</span></p>
          <p>Set <code className="bg-slate-100 px-1">GOOGLE_DRIVE_SA_JSON_PATH</code> and <code className="bg-slate-100 px-1">GOOGLE_DRIVE_FOLDER_ID</code> in <code>backend/.env</code> and restart.</p>
        </div>
      )}
    </div>
  );
}

function Info({ label, value, mono }) {
  return (
    <div>
      <div className="hk-tiny-label">{label}</div>
      <div className={`text-sm ${mono ? "font-mono break-all" : ""}`}>{value || "—"}</div>
    </div>
  );
}
